import { Alert, Box, Button, Link, Typography } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContentTitle, DialogTitle } from '~/components/dialogs/components';
import { DialogActions, DialogContent } from '~/components/dialogs/lib/styles';

const EXPORT_MAX = 250000;
const EXPORT_WARNING_THRESHOLD = 50000;

export interface ExportDialogProps {
  close: () => void;
  exportUrl: string | null;
  onExport: () => Promise<void>;
  exporting: boolean;
  totalCount: number | undefined;
}

export const ExportDialog = ({
  close,
  exportUrl,
  onExport,
  exporting,
  totalCount,
}: ExportDialogProps) => {
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);

  const handleExport = async () => {
    setError(null);
    try {
      await onExport();
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError(t('pages.devices.export_dialog.error_unexpected'));
      }
    }
  };

  const handleClose = () => {
    setError(null);
    close();
  };

  // If we have an export URL, show the success dialog
  if (exportUrl) {
    return (
      <Dialog fullWidth maxWidth="md" onClose={handleClose} open={Boolean(exportUrl)}>
        <DialogTitle onClose={handleClose}>
          {t('pages.devices.export_dialog.success_title')}
        </DialogTitle>
        <DialogContent>
          <DialogContentTitle>
            {t('pages.devices.export_dialog.success_message')}
          </DialogContentTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="subtitle1">
              {t('pages.devices.export_dialog.download_prompt')}
            </Typography>
            <Link sx={{ fontSize: '1.2em' }} target="_blank" rel="noreferrer" href={exportUrl}>
              {t('pages.devices.export_dialog.download_link_text')}
            </Link>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} variant="outlined">
            {t('common.actions.close')}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  // Otherwise show the export configuration dialog
  return (
    <Dialog fullWidth maxWidth="md" onClose={handleClose} open>
      <DialogTitle onClose={handleClose}>{t('pages.devices.export_dialog.title')}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <DialogContentTitle>
            {t('pages.devices.export_dialog.configure_message', { count: totalCount || 0 })}
          </DialogContentTitle>

          {totalCount && totalCount > EXPORT_MAX && (
            <Alert severity="error">
              {t('pages.devices.export_dialog.limit_error', { count: totalCount, max: EXPORT_MAX })}
            </Alert>
          )}

          {totalCount && totalCount > EXPORT_WARNING_THRESHOLD && totalCount <= EXPORT_MAX && (
            <Alert severity="warning">
              {t('pages.devices.export_dialog.limit_warning', { count: totalCount })}
            </Alert>
          )}

          {error && <Alert severity="error">{error}</Alert>}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} variant="outlined" disabled={exporting}>
          {t('common.actions.cancel')}
        </Button>
        <Button
          onClick={handleExport}
          variant="contained"
          disabled={exporting || (totalCount !== undefined && totalCount > EXPORT_MAX)}
        >
          {exporting ? t('common.buttons.exporting') : t('common.buttons.export')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
